/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBetaEnabled } from 'features';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router';
import { Alert, TextV2 } from '@insights-ltd/design-library/components';
import { arrayMatches } from '@insights-ltd/design-library/utils';
import { queryStatus } from 'utils/queryStatus';
import { Box, Button, Container, Link } from '@mui/material';
import Grid from '@mui/material/Grid';
import { FullScreenError, FullScreenSpinner } from 'components/FullScreen';
import {
  ColourScoreWithPreference,
  EvaluationsSearchResponse,
  LearnerPurchaseRequest,
  OrganisationResponse,
  Profile,
  useGetEvaluationsByOrgs,
  useGetPractitionerProducts,
  useGetPractitionerWallet,
} from 'api';
import { useAuth } from 'contexts/AuthContext';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { JungianColours, LicensedProduct, ProfileType } from 'types/types';
import { Dialects } from 'types/dialects';
import ChapterCheckboxes from './ChapterCheckboxes/ChapterCheckboxes';
import ProductRadioOptions from './ProductRadioOptions/ProductRadioOptions';
import ConfirmPurchase from './ConfirmPurchase/ConfirmPurchase';
import ConfirmDialog from './ConfirmDialog/ConfirmDialog';
import ProfileBasket from './ProfileBasket/ProfileBasket';
import PurchaseError from './PurchaseError';
import { getEvaluationSearches } from './PurchaseProfiles.helper';
import {
  ChapterName,
  LearnerForPurchase,
  ProfileTypes,
  PurchaseType,
} from './types';

const profileType: ProfileTypes = {
  DISCOVERY_SELF_AWARE_PROFILE: 'DISCOVERY_PROFILE',
  DISCOVERY_TRANSFORMATIONAL_LEADERSHIP_PROFILE: 'IDTL_PROFILE',
  DISCOVERY_IMPACT_PROFILE: 'IMPACT_PROFILE',
};

interface LatestEvaluation {
  id: string;
  submittedOn: string;
  organisation: OrganisationResponse;
}

interface ColourScores {
  conscious: ColourScoreWithPreference;
  lessConscious: ColourScoreWithPreference;
  opposite: ColourScoreWithPreference;
}

interface LatestProfile {
  id: string;
  createdAt: string;
  profileType: ProfileType;
  leadingColourEnergy: JungianColours;
  colourScores: ColourScores;
  evaluation: {
    id: string;
  };
  organisation: OrganisationResponse;
  chapters: ChapterName[];
}

interface LearnerData {
  primaryEmail: string;
  fullName: string;
  knownAs: string;
  preferredDialect: Dialects;
  latestEvaluation: LatestEvaluation;
  latestProfile: LatestProfile;
  id: string;
  learnerId: string;
}

type LearnersObject = Record<string, LearnerData>;

const getLatestProfile = (latestProfiles: Profile[], productName: string) => {
  return latestProfiles!.find(
    (profile) => profile.profileType === profileType[productName] || null,
  );
};

const extractPurchaseLines = (
  learnersWithCosts: LearnerForPurchase[],
  productName: string,
) => {
  return learnersWithCosts
    .filter((learner) => {
      const latestProfile = getLatestProfile(
        learner.latestEvaluation.latestProfiles,
        productName,
      );
      return learner.assignedChapters.length > 0 && !latestProfile;
    })
    .map((learner) => {
      return {
        learner: learner.id,
        evaluations: [learner.latestEvaluation.id],
        chapters: learner.assignedChapters.map(
          ({ name: chapterName }) => chapterName,
        ),
      };
    });
};

const extractUpdateLines = (
  learnersWithCosts: LearnerForPurchase[],
  productName: string,
) => {
  return learnersWithCosts
    .filter((learner) => {
      const latestProfile = getLatestProfile(
        learner.latestEvaluation.latestProfiles,
        productName,
      );
      return learner.assignedChapters.length > 0 && !!latestProfile;
    })
    .map((learner) => {
      return {
        profile: getLatestProfile(
          learner.latestEvaluation.latestProfiles,
          productName,
        )!.id,
        chapters: learner.assignedChapters.map(
          ({ name: chapterName }) => chapterName,
        ),
      };
    });
};

const getDefaultSelectedChapter = (product: LicensedProduct) => {
  switch (product) {
    case 'DISCOVERY_SELF_AWARE_PROFILE':
      return { FOUNDATION: true };
    case 'DISCOVERY_TRANSFORMATIONAL_LEADERSHIP_PROFILE':
      return { IDTL: true };
    case 'DISCOVERY_IMPACT_PROFILE':
      return { IMPACT: true };
    default:
      return {};
  }
};

const handleChangeOfProduct = (
  product: LicensedProduct,
  selectedChapters: Record<string, boolean | undefined>,
  cb: (chapters: Record<string, boolean | undefined>) => void,
) => {
  if (
    product === 'DISCOVERY_TRANSFORMATIONAL_LEADERSHIP_PROFILE' &&
    !selectedChapters.IDTL
  ) {
    cb(getDefaultSelectedChapter(product));
  }

  if (
    product === 'DISCOVERY_SELF_AWARE_PROFILE' &&
    !selectedChapters.FOUNDATION
  ) {
    cb(getDefaultSelectedChapter(product));
  }

  if (product === 'DISCOVERY_IMPACT_PROFILE' && !selectedChapters.IMPACT) {
    cb(getDefaultSelectedChapter(product));
  }
};

const backButtonTranslation: Record<PurchaseType, string> = {
  Evaluator:
    'ui.event-management.evaluator-links.purchase-page.back-button-label.evaluator',
  Chapters:
    'ui.event-management.evaluator-links.purchase-page.back-button-label.learner',
  Teams:
    'ui.event-management.evaluator-links.purchase-page.back-button-label.teams',
};

type PurchaseProductsProps = {
  isPurchasing: boolean;
  parentLink: string;
  organisationIds: string[];
  evaluationIds: string[];
  removeCheckedItems?: (items: string[]) => void;
  onPurchase: (
    requestData: LearnerPurchaseRequest,
    onSuccess: () => void,
    onFailure: () => void,
  ) => void;
  resetMutate: () => void;
  error: unknown | null;
  isError: boolean;
  type: PurchaseType;
};

const PurchaseProfiles = ({
  isPurchasing,
  organisationIds,
  parentLink,
  evaluationIds,
  removeCheckedItems,
  onPurchase,
  error,
  isError,
  resetMutate,
  type,
}: PurchaseProductsProps) => {
  const { t } = useTranslation();
  const [productName, setProductType] = useState<LicensedProduct>(
    'DISCOVERY_SELF_AWARE_PROFILE',
  );
  const [purchaseError, setPurchaseError] = useState<boolean>(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    learners,
    organisationId,
    evaluatorLinkOrganisationId,
    evaluatorLinkId,
  } = location.state || {};
  const isPersonalImpactProfileBetaEnabled = useBetaEnabled(
    'personal-impact-profile',
  );

  const backButtonText = t(backButtonTranslation[type]);

  const [selectedChapters, setSelectedChapters] = useState<
    Record<string, boolean | undefined>
  >(getDefaultSelectedChapter(productName));

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const { status: productStatus, data: products } = useGetPractitionerProducts(
    user!.id,
  );

  const [initialOrganisationIds] = useState(organisationIds);
  const [initialEvaluationIds] = useState(evaluationIds);
  const evaluationSearches = getEvaluationSearches(
    initialOrganisationIds,
    initialEvaluationIds,
    learners,
  );

  const { status: learnersStatus, data: loadedEvaluations = [] } =
    useGetEvaluationsByOrgs(evaluationSearches);
  const selectedEvaluationIds = new Set(evaluationIds);
  const evaluatorProfiles = loadedEvaluations.filter(({ id }) =>
    selectedEvaluationIds.has(id),
  );
  const {
    status: walletStatus,
    data: wallet,
    refetch,
  } = useGetPractitionerWallet(user!.id);

  const responseStatus = queryStatus(
    productStatus,
    walletStatus,
    learnersStatus,
  );

  if (responseStatus === 'pending') {
    return <FullScreenSpinner message={t('ui.event-management.loading')} />;
  }
  if (responseStatus === 'error') {
    return (
      <FullScreenError
        message={t(
          'ui.event-management.events.download-profiles.error-loading-learners',
        )}
      />
    );
  }

  if (isExploreClassic(type, evaluatorProfiles)) {
    navigate(parentLink);
  }

  const purchaseProducts = products!.products;
  const productNames: LicensedProduct[] = purchaseProducts.map(
    ({ name }) => name,
  );

  const { availableUnits: walletBalance } = wallet!;

  const handleClose = () => setIsConfirmOpen(false);

  const chapters = purchaseProducts
    .filter((product) => product.name === productName)
    .flatMap((p) => p.chapters);

  const chapterNames = chapters.map(({ name }) => name);
  const learnersWithCosts: LearnerForPurchase[] = evaluatorProfiles.map(
    (evaluation) => {
      const { learner, ...latestEvaluation } = evaluation;
      const purchasedChapters =
        getLatestProfile(
          latestEvaluation.latestProfiles,
          productName,
        )?.chapters.filter((product) => chapterNames.includes(product)) || [];
      const learnerCost = chapters.reduce(
        (acc, { name: chapterName, cost }) => {
          return !purchasedChapters.includes(chapterName) &&
            selectedChapters[chapterName]
            ? cost + acc
            : acc;
        },
        0,
      );

      const matchingLearner = learners
        ? Object.values(learners as LearnersObject).find(
            (learnerData) => learnerData.learnerId === learner.id,
          )
        : undefined;

      return {
        ...learner,
        latestEvaluation,
        purchaseTotal: learnerCost ?? 0,
        assignedChapters: chapters.filter(
          (chapter) =>
            selectedChapters[chapter.name] &&
            !purchasedChapters.includes(chapter.name),
        ),
        purchasedChapters,
        organisationId:
          matchingLearner !== undefined
            ? matchingLearner?.latestEvaluation?.organisation?.id
            : organisationId,
      };
    },
  );

  const transactionTotal = learnersWithCosts.reduce(
    (acc, { purchaseTotal }) => (purchaseTotal ? acc + purchaseTotal : acc),
    0,
  );

  const handlePurchase = () => {
    const selectedProduct =
      isPersonalImpactProfileBetaEnabled ||
      productName !== 'DISCOVERY_IMPACT_PROFILE'
        ? productName
        : 'DISCOVERY_SELF_AWARE_PROFILE';

    setIsConfirmOpen(true);
    const requestData = {
      product: selectedProduct,
      createProfiles: extractPurchaseLines(learnersWithCosts, selectedProduct),
      updateProfiles: extractUpdateLines(learnersWithCosts, selectedProduct),
    };

    if (!isError) {
      onPurchase(
        requestData,
        () => {
          setIsConfirmOpen(false);
          navigate(parentLink);
        },
        () => {
          setIsConfirmOpen(false);
        },
      );
    }
  };

  const handleChecked = (name: string, selected: boolean) => {
    let selectedItems;
    if (selected) {
      const { [name]: oldName, ...remainingChecked } = selectedChapters;
      selectedItems = remainingChecked;
    } else {
      selectedItems = {
        ...selectedChapters,
        [name]: true,
      };
    }
    setSelectedChapters(selectedItems);
  };

  const postTransactionBalance = walletBalance - transactionTotal || 0;
  const isInvalidTransaction = transactionTotal > walletBalance;

  const learnersCount = evaluatorProfiles.length;

  let prePurchasedChapters: string[];
  let disabledProducts: LicensedProduct[] = [];

  switch (type) {
    case 'Chapters':
      prePurchasedChapters =
        evaluatorProfiles[0]?.latestProfiles.find(
          (profile) => profile?.profileType === profileType[productName],
        )?.chapters ?? [];
      disabledProducts = evaluatorProfiles[0]?.latestProfiles.find(
        (profile) =>
          profile?.profileType === profileType.DISCOVERY_SELF_AWARE_PROFILE,
      )
        ? []
        : [
            'DISCOVERY_TRANSFORMATIONAL_LEADERSHIP_PROFILE',
            'DISCOVERY_IMPACT_PROFILE',
          ];
      break;
    case 'Evaluator':
    case 'Teams':
      prePurchasedChapters = chapters.reduce((arr, { name: chapterName }) => {
        const total = learnersWithCosts.filter((learner) =>
          learner.purchasedChapters.includes(chapterName),
        ).length;
        if (total === learnersCount) arr.push(chapterName);
        return arr;
      }, [] as string[]);
      break;
    default:
      prePurchasedChapters = [];
      disabledProducts = [];
  }

  if (!isPersonalImpactProfileBetaEnabled) {
    disabledProducts = [...disabledProducts, 'DISCOVERY_IMPACT_PROFILE'];
  }

  if (
    prePurchasedChapters?.length > 0 &&
    prePurchasedChapters?.length > Object.keys(selectedChapters).length &&
    !arrayMatches(Object.keys(selectedChapters), prePurchasedChapters)
  ) {
    const prePurchasedMap = prePurchasedChapters.reduce(
      (acc, current) => ({ ...acc, [current]: true }),
      {},
    );
    setSelectedChapters({
      ...selectedChapters,
      ...prePurchasedMap,
    });
  }

  handleChangeOfProduct(productName, selectedChapters, setSelectedChapters);

  if (evaluationIds.length === 0) navigate(parentLink);

  return (
    <Container maxWidth="lg">
      <Box sx={(th) => ({ marginBottom: th.spacing(spacingSizeMap.L) })}>
        <TextV2 component="h1" variant="h2">
          {t('ui.event-management.learners.purchase-profiles.page-title')}
        </TextV2>
      </Box>
      <Box
        sx={(th) => ({
          '& > p': {
            fontWeight: th.typography.fontWeightBold,
          },
        })}
      >
        <TextV2 component="p">
          {t('ui.event-management.learners.purchase-profiles.which-product')}
        </TextV2>
      </Box>
      <Box sx={{ marginBottom: '2.5rem' }}>
        <ProductRadioOptions
          onChange={setProductType}
          productValue={productName}
          disabledValues={disabledProducts}
          practitionerProducts={productNames}
          showPersonalImpactProfile={isPersonalImpactProfileBetaEnabled}
        />
      </Box>
      <Box sx={(th) => ({ marginBottom: th.spacing(spacingSizeMap.S) })}>
        <TextV2 component="h1" variant="h3">
          {t(
            'ui.event-management.learners.purchase-profiles.your-basket.heading',
          )}
        </TextV2>
      </Box>
      {productName === 'DISCOVERY_SELF_AWARE_PROFILE' ? (
        <>
          <Box sx={(th) => ({ marginBottom: th.spacing(spacingSizeMap.XS) })}>
            {type === 'Evaluator' ? (
              <TextV2 component="p">
                {t(
                  'ui.event-management.learners.purchase-profiles.your-basket.assign-chapters.description',
                )}
              </TextV2>
            ) : null}
          </Box>
          <Box sx={(th) => ({ marginBottom: th.spacing(spacingSizeMap.XS) })}>
            <ChapterCheckboxes
              chapters={chapters}
              checkedChapters={selectedChapters}
              handleCheckedChapter={handleChecked}
              prePurchasedChapters={prePurchasedChapters}
            />
          </Box>
        </>
      ) : null}
      <ProfileBasket
        purchaseItems={learnersWithCosts}
        evaluatorLinkOrganisationId={evaluatorLinkOrganisationId}
        evaluatorLinkId={evaluatorLinkId}
        organisationIds={organisationIds}
        removeCheckedItems={removeCheckedItems}
        productName={productName}
        setPurchaseError={setPurchaseError}
        purchaseError={purchaseError}
      />
      {purchaseError ? (
        <Grid
          sx={(theme) => ({
            marginTop: theme.spacing(spacingSizeMap.S),
            width: '720px',
            [theme.breakpoints.down('lg')]: {
              width: '100%',
            },
          })}
        >
          <Alert severity="error">
            {t(
              'ui.event-management.learners.purchase-profiles.confirm-purchase.basket-error',
            )}
          </Alert>
        </Grid>
      ) : null}
      <Grid container>
        <Grid
          container
          spacing={0}
          sx={{
            marginLeft: '-4px',
            marginTop: '2.5rem',
            width: '100%',
            marginBottom: '0.5rem',
          }}
        >
          {!purchaseError ? (
            <ConfirmPurchase
              userUnitBalance={walletBalance}
              transactionTotal={transactionTotal}
              postTransactionBalance={postTransactionBalance}
              isInvalidTransaction={isInvalidTransaction}
            />
          ) : null}
          <Grid size={12} sx={{ p: 0.5 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
              }}
            >
              <Link component={RouterLink} underline="none" to={parentLink}>
                <Button variant="outlined" color="primary">
                  {backButtonText}
                </Button>
              </Link>
              {!purchaseError ? (
                <Button
                  disabled={
                    isInvalidTransaction ||
                    transactionTotal === 0 ||
                    isPurchasing
                  }
                  variant="contained"
                  color="primary"
                  onClick={handlePurchase}
                >
                  {t(
                    'ui.event-management.learners.purchase-profiles.confirm-purchase.button-label',
                  )}
                </Button>
              ) : null}
            </Box>
          </Grid>
        </Grid>
        {isConfirmOpen && !isError ? (
          <ConfirmDialog
            handleClose={handleClose}
            open={isConfirmOpen && !isError}
          />
        ) : null}
        {isError ? (
          <PurchaseError
            error={error}
            isError={isError}
            reset={resetMutate}
            resetWallet={refetch}
          />
        ) : null}
      </Grid>
    </Container>
  );
};

export default PurchaseProfiles;
/* eslint-enable @typescript-eslint/no-non-null-assertion */

function isExploreClassic(
  type: PurchaseType,
  evaluatorProfiles: EvaluationsSearchResponse[],
) {
  if (type === 'Chapters' && evaluatorProfiles.length > 0) {
    if (evaluatorProfiles[0].evaluatorType === 'CLASSIC_EXPLORE') {
      return true;
    }
  }

  return false;
}
